import { JSX, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  FieldPath,
  UseFormReturn,
  useController,
  useWatch,
} from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';
import { Box, CheckIcon, EditIcon, Info, TrashIcon, XIcon } from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Label } from 'components/Label';
import {
  BaseOptionType,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectValue as SelectValueType,
} from 'components/Select';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Input } from 'components/input';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { useToast } from 'hooks/useToaster';
import { searchCommodities } from 'lib/api/searchCommodities';
import { HANDLING_UNIT_TYPES_THREEG } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { formatUpToFiveDecimals } from 'pages/QuoteView/Quoting/RequestQuickQuote/utils/formatting';
import { NormalizedLoad, TMSCommodity } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { calculateDensity } from 'utils/calculateDensity';
import {
  NMFC_DENSITY_FREIGHT_CLASSES,
  densityToFreightClass,
} from 'utils/densityToFreightClass';
import { cn } from 'utils/shadcn';

import { weightUnitsList } from '../constants';
import {
  buildItemDimensionsKey,
  calculateCubicFeet,
  getDensityDimensionUnitId,
  getDensityWeightUnitId,
  getPositiveFiniteNumber,
} from '../utils/ltlItemMetrics';

const FIELD_PATH = 'commodities';
const DEFAULT_HU_TYPE_VALUE =
  HANDLING_UNIT_TYPES_THREEG.find((o) => o.value === '3')?.value ?? '3';

interface CommodityItemCardProps {
  index: number;
  formMethods: UseFormReturn<NormalizedLoad>;
  onRemove: (index: number) => void;
  fieldPath?: 'commodities';
  commodities?: TMSCommodity[];
  isLoadingCommodities?: boolean;
  tmsID?: number;
  customerID?: number;
  hasCustomer?: boolean;
  hasCustomerId?: boolean;
  enableAutoFreightClass?: boolean;
}

type CommodityOption = BaseOptionType & {
  freightClass?: string;
  nmfcCode?: string;
  commodityName?: string;
  isCustom?: boolean;
  renderLabel?: string;
};

const ANIMATIONS = {
  card: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  },
  toggle: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.15 },
  },
  actions: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.1 },
  },
};

function getCommodityDisplayName(commodity: TMSCommodity): string {
  return (
    commodity.commodityDescription ??
    commodity.commodity ??
    commodity.externalTMSID ??
    ''
  );
}

function getCommodityFreightClass(commodity: TMSCommodity): string {
  return (
    commodity.freightClass?.trim() ||
    (commodity.freightClassId != null ? String(commodity.freightClassId) : '')
  );
}

function hasAnyItemData(
  item: Undef<Partial<NormalizedLoad['commodities'][0]>>
): boolean {
  if (!item) return false;
  if (item.description?.trim()) return true;
  if (item.externalTmsId?.trim()) return true;
  if (item.freightClass?.trim()) return true;
  if (item.nmfc?.trim()) return true;
  const w = Number(item.weightTotal) || 0;
  const hu = Number(item.handlingQuantity) || 0;
  const qty = Number(item.quantity) || 0;
  const l = Number(item.length) || 0;
  const wd = Number(item.width) || 0;
  const h = Number(item.height) || 0;
  return w > 0 || hu > 0 || qty > 0 || l > 0 || wd > 0 || h > 0;
}

function getCommodityPlaceholder({
  hasCustomer,
  isLoadingCommodities,
  isSearchingCommodities,
  commoditiesForDropdownLength,
  hasCustomerId,
}: {
  hasCustomer: boolean;
  isLoadingCommodities: boolean;
  isSearchingCommodities: boolean;
  commoditiesForDropdownLength: number;
  hasCustomerId: boolean;
}): string {
  if (!hasCustomer) {
    return 'Select a customer first to load commodities';
  }
  if (isLoadingCommodities) {
    return 'Loading commodities...';
  }
  if (isSearchingCommodities) {
    return 'Searching commodities...';
  }
  if (commoditiesForDropdownLength === 0) {
    return 'No commodities found — type to enter manually';
  }
  if (hasCustomerId) {
    return 'Select or type a description';
  }
  return 'Select or type a description; live search requires a synced customer';
}

export function CommodityItemCard({
  index,
  formMethods,
  onRemove,
  fieldPath = FIELD_PATH,
  commodities = [],
  isLoadingCommodities = false,
  tmsID,
  customerID,
  hasCustomer = false,
  hasCustomerId = false,
  enableAutoFreightClass = false,
}: CommodityItemCardProps): JSX.Element {
  const {
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { dirtyFields },
  } = formMethods;
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] =
    useState<Maybe<Partial<NormalizedLoad['commodities'][0]>>>(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchedCommodities, setSearchedCommodities] = useState<
    TMSCommodity[]
  >([]);
  const [isSearchingCommodities, setIsSearchingCommodities] = useState(false);
  const commoditySearchRequestIdRef = useRef(0);
  const commodityApplyDimsKeyRef = useRef<string | null>(null);
  const lastEstimatedFreightClassRef = useRef<string | null>(null);

  const watchedItem = useWatch({
    control,
    name: `${fieldPath}.${index}` as FieldPath<NormalizedLoad>,
  }) as Undef<NormalizedLoad['commodities'][0]>;
  const [
    watchedLength,
    watchedWidth,
    watchedHeight,
    watchedWeightTotal,
    watchedHandlingQuantity,
    watchedWeightUnit,
    watchedDimensionUnit,
  ] = useWatch({
    control,
    name: [
      `${fieldPath}.${index}.length`,
      `${fieldPath}.${index}.width`,
      `${fieldPath}.${index}.height`,
      `${fieldPath}.${index}.weightTotal`,
      `${fieldPath}.${index}.handlingQuantity`,
      `${fieldPath}.${index}.weightUnit`,
      `${fieldPath}.${index}.dimensionUnit`,
    ] as FieldPath<NormalizedLoad>[],
  });
  const hasData = hasAnyItemData(watchedItem);
  const basePath = `${fieldPath}.${index}`;
  const descriptionPath =
    `${basePath}.description` as FieldPath<NormalizedLoad>;
  const {
    fieldState: { error: descriptionError },
  } = useController({
    name: descriptionPath,
    control,
    rules: {
      validate: (value) =>
        Boolean(String(value ?? '').trim()) || 'Description is required',
    },
  });

  useEffect(() => {
    if ((!hasData || descriptionError) && !isEditing) {
      setOriginalValues({ ...watchedItem });
      setIsEditing(true);
    }
  }, [descriptionError, hasData, isEditing, watchedItem]);

  const handlers = {
    edit: () => {
      setOriginalValues({ ...watchedItem });
      setIsEditing(true);
    },
    save: () => {
      const description = getValues(descriptionPath);
      if (!String(description ?? '').trim()) {
        setError(descriptionPath, {
          type: 'required',
          message: 'Description is required',
        });
        return;
      }
      clearErrors(descriptionPath);

      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      const wasNewEmptyItem = !hasAnyItemData(originalValues ?? undefined);
      if (wasNewEmptyItem) {
        onRemove(index);
        return;
      }
      if (originalValues) {
        Object.keys(originalValues).forEach((key) => {
          const fieldName = `${fieldPath}.${index}.${key}`;
          const value = originalValues[key as keyof typeof originalValues];
          if (value !== undefined) {
            formMethods.setValue(fieldName as any, value, {
              shouldDirty: false,
            });
          }
        });
      }
      clearErrors(descriptionPath);
      setIsEditing(false);
      setOriginalValues(null);
    },
    remove: () => onRemove(index),
  };

  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1'
        >
          <ActionButton
            icon={CheckIcon}
            onClick={handlers.save}
            className='hover:bg-info-200 text-info-600'
            aria-label='Save changes'
          />
          <ActionButton
            icon={XIcon}
            onClick={handlers.discard}
            className='hover:bg-error-200 text-error-600'
            aria-label='Discard changes'
          />
        </motion.div>
      ) : (
        <motion.div
          key='viewing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1'
        >
          <ActionButton
            icon={EditIcon}
            onClick={handlers.edit}
            className='hover:bg-info-300 text-info-600'
            aria-label='Edit item'
          />
          <ActionButton
            icon={TrashIcon}
            onClick={handlers.remove}
            className='hover:bg-error-400 text-error-600'
            aria-label='Remove item'
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && isEditing) {
      e.preventDefault();
      e.stopPropagation();
      handlers.save();
    }
  };

  const descriptionValue = watchedItem?.description?.trim() ?? '';
  const selectedCommodityExternalTmsId = watchedItem?.externalTmsId ?? '';
  const positiveLength = getPositiveFiniteNumber(watchedLength);
  const positiveWidth = getPositiveFiniteNumber(watchedWidth);
  const positiveHeight = getPositiveFiniteNumber(watchedHeight);
  const positiveWeightTotal = getPositiveFiniteNumber(watchedWeightTotal);
  const effectiveHandlingQuantity =
    getPositiveFiniteNumber(watchedHandlingQuantity) ?? 1;
  const dimensionUnitId = getDensityDimensionUnitId(watchedDimensionUnit);
  const itemCubicFeet = useMemo(() => {
    return calculateCubicFeet({
      length: watchedLength,
      width: watchedWidth,
      height: watchedHeight,
      handlingQuantity: watchedHandlingQuantity,
      dimensionUnitId,
    });
  }, [
    dimensionUnitId,
    watchedHandlingQuantity,
    watchedHeight,
    watchedLength,
    watchedWidth,
  ]);

  const calculatedDensityPcf = useMemo(
    () =>
      calculateDensity(
        positiveWeightTotal ?? null,
        getDensityWeightUnitId(watchedWeightUnit),
        positiveLength ?? null,
        positiveWidth ?? null,
        positiveHeight ?? null,
        dimensionUnitId,
        effectiveHandlingQuantity
      ),
    [
      dimensionUnitId,
      effectiveHandlingQuantity,
      positiveHeight,
      positiveLength,
      positiveWeightTotal,
      positiveWidth,
      watchedWeightUnit,
    ]
  );
  const pcf = calculatedDensityPcf ?? 0;
  const densityDerivedFreightClassString =
    pcf > 0 ? densityToFreightClass(pcf) : null;
  const trimmedSearchValue = searchValue.trim();
  const shouldUseCommoditySearchAPI = commodities.length >= 100;
  const hasActiveSearch =
    shouldUseCommoditySearchAPI &&
    trimmedSearchValue.length >= 2 &&
    Boolean(tmsID && customerID);

  useEffect(() => {
    if (!hasActiveSearch || !tmsID || !customerID) {
      commoditySearchRequestIdRef.current += 1;
      setSearchedCommodities([]);
      setIsSearchingCommodities(false);
      return;
    }

    const requestId = ++commoditySearchRequestIdRef.current;
    const timer = setTimeout(async () => {
      setIsSearchingCommodities(true);
      const result = await searchCommodities(
        tmsID,
        customerID,
        trimmedSearchValue
      );

      if (requestId !== commoditySearchRequestIdRef.current) {
        return;
      }

      if (result.isOk()) {
        setSearchedCommodities(result.value.commodityList ?? []);
      } else {
        setSearchedCommodities([]);
        toast({
          description: result.error.message ?? 'Failed to search commodities',
          variant: 'destructive',
        });
      }
      setIsSearchingCommodities(false);
    }, 300);

    return () => {
      commoditySearchRequestIdRef.current += 1;
      clearTimeout(timer);
    };
  }, [customerID, hasActiveSearch, tmsID, toast, trimmedSearchValue]);

  const selectedCommodityFallback = useMemo(() => {
    if (!selectedCommodityExternalTmsId) {
      return undefined;
    }

    return {
      externalTMSID: selectedCommodityExternalTmsId,
      commodityDescription: descriptionValue,
      freightClass: watchedItem?.freightClass ?? '',
      nmfc: watchedItem?.nmfc ?? '',
    } as TMSCommodity;
  }, [
    descriptionValue,
    selectedCommodityExternalTmsId,
    watchedItem?.freightClass,
    watchedItem?.nmfc,
  ]);

  const allCommodityOptions = useMemo(() => {
    const byExternalId = new Map<string, TMSCommodity>();
    for (const commodity of [
      ...(selectedCommodityFallback ? [selectedCommodityFallback] : []),
      ...commodities,
      ...searchedCommodities,
    ]) {
      if (commodity.externalTMSID) {
        byExternalId.set(commodity.externalTMSID, commodity);
      }
    }
    return Array.from(byExternalId.values());
  }, [commodities, searchedCommodities, selectedCommodityFallback]);

  const selectedCommodity = useMemo(() => {
    if (!selectedCommodityExternalTmsId) return undefined;
    return allCommodityOptions.find(
      (commodity) => commodity.externalTMSID === selectedCommodityExternalTmsId
    );
  }, [allCommodityOptions, selectedCommodityExternalTmsId]);

  const commoditiesForDropdown = useMemo(() => {
    if (!enableAutoFreightClass || densityDerivedFreightClassString == null) {
      return commodities;
    }

    return commodities.filter(
      (commodity) =>
        getCommodityFreightClass(commodity) === densityDerivedFreightClassString
    );
  }, [commodities, densityDerivedFreightClassString, enableAutoFreightClass]);

  const searchedCommoditiesForDropdown = useMemo(() => {
    if (!enableAutoFreightClass || densityDerivedFreightClassString == null) {
      return searchedCommodities;
    }

    return searchedCommodities.filter(
      (commodity) =>
        getCommodityFreightClass(commodity) === densityDerivedFreightClassString
    );
  }, [
    densityDerivedFreightClassString,
    enableAutoFreightClass,
    searchedCommodities,
  ]);

  const commodityOptions = useMemo<CommodityOption[]>(() => {
    const source = hasActiveSearch
      ? searchedCommoditiesForDropdown
      : commoditiesForDropdown;
    const baseOptions: CommodityOption[] = source.map((commodity) => ({
      label: getCommodityDisplayName(commodity),
      value: commodity.externalTMSID,
      freightClass: getCommodityFreightClass(commodity) || undefined,
      nmfcCode: commodity.nmfc ?? commodity.nmfcNumber,
      commodityName: commodity.commodity,
    }));

    const options = [...baseOptions];
    if (
      selectedCommodity?.externalTMSID &&
      !options.some(
        (option) => option.value === selectedCommodity.externalTMSID
      )
    ) {
      options.unshift({
        label: getCommodityDisplayName(selectedCommodity),
        value: selectedCommodity.externalTMSID,
        freightClass: getCommodityFreightClass(selectedCommodity) || undefined,
        nmfcCode: selectedCommodity.nmfc ?? selectedCommodity.nmfcNumber,
        commodityName: selectedCommodity.commodity,
      });
    }

    const normalizedSearch = searchValue.trim();
    const isSearchMatchingCommodity = normalizedSearch
      ? baseOptions.some((option) => {
          const label = String(option.label).toLowerCase();
          return (
            label === normalizedSearch.toLowerCase() ||
            String(option.value).toLowerCase() ===
              normalizedSearch.toLowerCase()
          );
        })
      : false;

    const customOption = descriptionValue
      ? {
          label: descriptionValue,
          value: `custom:${descriptionValue}`,
          isCustom: true,
          renderLabel: descriptionValue,
        }
      : null;
    const searchOption =
      normalizedSearch && !isSearchMatchingCommodity
        ? {
            label: normalizedSearch,
            value: `custom:${normalizedSearch}`,
            isCustom: true,
            renderLabel: `Use "${normalizedSearch}"`,
          }
        : null;

    if (
      customOption &&
      !options.some((option) => option.value === customOption.value)
    ) {
      options.unshift(customOption);
    }
    if (
      searchOption &&
      !options.some((option) => option.value === searchOption.value)
    ) {
      options.unshift(searchOption);
    }

    return options;
  }, [
    commoditiesForDropdown,
    descriptionValue,
    hasActiveSearch,
    searchValue,
    searchedCommoditiesForDropdown,
    selectedCommodity,
  ]);

  const selectValue =
    selectedCommodityExternalTmsId ||
    (descriptionValue ? `custom:${descriptionValue}` : undefined);

  const commodityPlaceholder = getCommodityPlaceholder({
    hasCustomer,
    isLoadingCommodities,
    isSearchingCommodities,
    commoditiesForDropdownLength: commoditiesForDropdown.length,
    hasCustomerId,
  });

  const filterCommodityOption = (input: string, option?: BaseOptionType) => {
    if (!option) return false;
    const typedOption = option as CommodityOption;
    if (typedOption.isCustom) return true;
    const search = input.toLowerCase();
    return (
      String(typedOption.label ?? '')
        .toLowerCase()
        .includes(search) ||
      (typedOption.commodityName ?? '').toLowerCase().includes(search) ||
      (typedOption.nmfcCode ?? '').toLowerCase().includes(search)
    );
  };

  const applyCommoditySelection = (commodity: TMSCommodity) => {
    const displayName = getCommodityDisplayName(commodity);
    setValue(`${basePath}.description` as any, displayName, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue(
      `${basePath}.externalTmsId` as any,
      commodity.externalTMSID ?? '',
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
    setValue(
      `${basePath}.freightClass` as any,
      getCommodityFreightClass(commodity),
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
    setValue(
      `${basePath}.nmfc` as any,
      commodity.nmfc ?? commodity.nmfcNumber ?? '',
      {
        shouldValidate: true,
        shouldDirty: true,
      }
    );
    setValue(`${basePath}.matchedToTMSCommodity` as any, true, {
      shouldDirty: true,
    });

    // Match Quick Quote: 3G returns zero/missing values for unset commodity
    // defaults, so keep existing broker-entered values instead of clearing them.
    if (commodity.length && commodity.length > 0) {
      setValue(`${basePath}.length` as any, commodity.length, {
        shouldDirty: true,
      });
    }
    if (commodity.width && commodity.width > 0) {
      setValue(`${basePath}.width` as any, commodity.width, {
        shouldDirty: true,
      });
    }
    if (commodity.height && commodity.height > 0) {
      setValue(`${basePath}.height` as any, commodity.height, {
        shouldDirty: true,
      });
    }
    if (commodity.weightAmount && commodity.weightAmount > 0) {
      setValue(`${basePath}.weightTotal` as any, commodity.weightAmount, {
        shouldDirty: true,
      });
    }
    if (commodity.handlingUnitCount && commodity.handlingUnitCount > 0) {
      setValue(
        `${basePath}.handlingQuantity` as any,
        commodity.handlingUnitCount,
        { shouldDirty: true }
      );
    }
    if (commodity.pieceCount && commodity.pieceCount > 0) {
      setValue(`${basePath}.quantity` as any, commodity.pieceCount, {
        shouldDirty: true,
      });
    }
    if (commodity.handlingUnitTypeId != null) {
      setValue(
        `${basePath}.packagingType` as any,
        String(commodity.handlingUnitTypeId),
        {
          shouldDirty: true,
        }
      );
    }
    if (commodity.stackable != null) {
      setValue(`${basePath}.stackable` as any, commodity.stackable, {
        shouldDirty: true,
      });
    }

    const nextLength =
      getPositiveFiniteNumber(commodity.length) ?? watchedLength;
    const nextWidth = getPositiveFiniteNumber(commodity.width) ?? watchedWidth;
    const nextHeight =
      getPositiveFiniteNumber(commodity.height) ?? watchedHeight;
    const nextWeightTotal =
      getPositiveFiniteNumber(commodity.weightAmount) ?? watchedWeightTotal;
    const nextHandlingQuantity =
      getPositiveFiniteNumber(commodity.handlingUnitCount) ??
      watchedHandlingQuantity;

    commodityApplyDimsKeyRef.current = buildItemDimensionsKey({
      length: nextLength,
      width: nextWidth,
      height: nextHeight,
      weight: nextWeightTotal,
      handlingQuantity: nextHandlingQuantity,
      weightUnit: watchedWeightUnit,
      dimensionUnit: watchedDimensionUnit,
    });
  };

  useEffect(() => {
    if (!enableAutoFreightClass) {
      return;
    }

    if (!watchedItem?.externalTmsId) {
      commodityApplyDimsKeyRef.current = null;
    }

    const currentDimsKey = buildItemDimensionsKey({
      length: watchedLength,
      width: watchedWidth,
      height: watchedHeight,
      weight: watchedWeightTotal,
      handlingQuantity: watchedHandlingQuantity,
      weightUnit: watchedWeightUnit,
      dimensionUnit: watchedDimensionUnit,
    });
    const dimsStillMatchAppliedCommodity =
      commodityApplyDimsKeyRef.current != null &&
      commodityApplyDimsKeyRef.current === currentDimsKey &&
      Boolean(watchedItem?.externalTmsId);

    if (densityDerivedFreightClassString == null) {
      if (watchedItem?.externalTmsId) {
        return;
      }

      const currentFreightClass = watchedItem?.freightClass?.trim() ?? '';
      if (currentFreightClass !== '') {
        setValue(`${basePath}.freightClass` as any, '', {
          shouldDirty: false,
          shouldValidate: true,
        });
      }
      return;
    }

    if (dimsStillMatchAppliedCommodity) {
      return;
    }

    const nextFreightClass = densityDerivedFreightClassString;
    const currentFreightClass = watchedItem?.freightClass?.trim() ?? '';
    if (currentFreightClass !== nextFreightClass) {
      setValue(`${basePath}.freightClass` as any, nextFreightClass, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }

    if (!watchedItem?.externalTmsId || isLoadingCommodities) {
      return;
    }

    const selectedFreightClass = selectedCommodity
      ? getCommodityFreightClass(selectedCommodity)
      : '';
    if (selectedFreightClass !== nextFreightClass) {
      commodityApplyDimsKeyRef.current = null;
      setValue(`${basePath}.externalTmsId` as any, '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`${basePath}.description` as any, '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`${basePath}.nmfc` as any, '', {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`${basePath}.matchedToTMSCommodity` as any, false, {
        shouldDirty: true,
      });
    }
  }, [
    basePath,
    densityDerivedFreightClassString,
    enableAutoFreightClass,
    isLoadingCommodities,
    selectedCommodity,
    setValue,
    watchedHandlingQuantity,
    watchedHeight,
    watchedItem?.externalTmsId,
    watchedItem?.freightClass,
    watchedDimensionUnit,
    watchedLength,
    watchedWeightTotal,
    watchedWeightUnit,
    watchedWidth,
  ]);

  useEffect(() => {
    if (enableAutoFreightClass) {
      lastEstimatedFreightClassRef.current = null;
      return;
    }
    if ((dirtyFields.commodities?.[index] as any)?.freightClass) {
      lastEstimatedFreightClassRef.current = null;
      return;
    }
    const currentFreightClass = watchedItem?.freightClass?.trim() ?? '';
    if (pcf <= 0) {
      if (
        currentFreightClass &&
        currentFreightClass === lastEstimatedFreightClassRef.current
      ) {
        setValue(`${basePath}.freightClass` as any, '', {
          shouldDirty: false,
          shouldValidate: true,
        });
        lastEstimatedFreightClassRef.current = null;
      }
      return;
    }

    const nextClass = densityToFreightClass(pcf);
    if (currentFreightClass === nextClass) {
      return;
    }

    lastEstimatedFreightClassRef.current = nextClass;
    setValue(`${basePath}.freightClass` as any, nextClass, {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [
    basePath,
    dirtyFields.commodities,
    enableAutoFreightClass,
    index,
    pcf,
    setValue,
    watchedItem?.freightClass,
  ]);

  const handleCommoditySelect = (
    value: SelectValueType,
    option?: BaseOptionType | BaseOptionType[]
  ) => {
    const selectedOption = Array.isArray(option) ? option[0] : option;
    const selectedValue =
      typeof value === 'string' || typeof value === 'number'
        ? String(value)
        : '';

    if ((selectedOption as CommodityOption | undefined)?.isCustom) {
      const customLabel = String(selectedOption?.label ?? '').trim();
      const hadSelectedCommodity = Boolean(watchedItem?.externalTmsId);
      setValue(`${basePath}.description` as any, customLabel, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${basePath}.externalTmsId` as any, '', {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${basePath}.matchedToTMSCommodity` as any, false, {
        shouldDirty: true,
      });

      if (hadSelectedCommodity) {
        setValue(`${basePath}.freightClass` as any, '', {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.nmfc` as any, '', {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.length` as any, 0, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.width` as any, 0, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.height` as any, 0, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.weightTotal` as any, 0, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.handlingQuantity` as any, 0, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.quantity` as any, 0, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.packagingType` as any, DEFAULT_HU_TYPE_VALUE, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.handlingUnitTypeName` as any, '', {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`${basePath}.stackable` as any, false, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      setSearchValue('');
      return;
    }

    const commodity = allCommodityOptions.find(
      (candidate) => candidate.externalTMSID === selectedValue
    );
    if (commodity) {
      applyCommoditySelection(commodity);
    }
    setSearchValue('');
  };

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden w-full'
    >
      <Flex justify='between' align='center' className='mb-3'>
        <Typography variant='label' className='text-neutral-900'>
          Edit Item
        </Typography>
        <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
      </Flex>

      <div className='mb-3'>
        <Label
          name={`${basePath}.description`}
          hideAIHint={true}
          required
          className='text-sm text-neutral-900'
        >
          Description
        </Label>
        <Select
          value={selectValue}
          onChange={handleCommoditySelect}
          options={commodityOptions}
          showSearch
          onSearch={setSearchValue}
          placeholder={commodityPlaceholder}
          className='w-full mt-1'
          filterOption={hasActiveSearch ? false : filterCommodityOption}
          optionRender={(option: CommodityOption) => {
            if (option.isCustom) {
              return (
                <span className='block whitespace-normal break-words'>
                  {option.renderLabel ?? option.label}
                </span>
              );
            }

            return (
              <span className='block whitespace-normal break-words'>
                {option.label}
                {option.freightClass ? ` (CL ${option.freightClass})` : ''}
              </span>
            );
          }}
        />
        {descriptionError?.message && (
          <Typography variant='body-xs' className='text-error-500 mt-1'>
            {descriptionError.message}
          </Typography>
        )}
      </div>

      <Grid cols='2' gap='lg' className='w-full mx-0 mb-3'>
        <div>
          <Label
            name={`${basePath}.freightClass`}
            hideAIHint={true}
            required={false}
            className='text-sm text-neutral-900'
          >
            Freight Class
          </Label>
          <Controller
            name={`${basePath}.freightClass` as FieldPath<NormalizedLoad>}
            control={control}
            render={({ field }) => (
              <div className='mt-1 text-neutral-500'>
                <Select
                  onValueChange={(v) => field.onChange(v ?? undefined)}
                  value={String(field.value ?? '')}
                  disabled={enableAutoFreightClass}
                >
                  <SelectTrigger className='w-full text-neutral-500'>
                    <SelectValue placeholder='Select class' />
                  </SelectTrigger>
                  <SelectContent>
                    {NMFC_DENSITY_FREIGHT_CLASSES.map((freightClass) => (
                      <SelectItem key={freightClass} value={freightClass}>
                        {freightClass}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          />
        </div>
        <RHFTextInput
          name={`${basePath}.nmfc`}
          label='NMFC'
          placeholder='e.g., 63160-03'
          required={false}
        />
      </Grid>

      <Grid cols='2' gap='lg' className='w-full mx-0 mb-3'>
        <div>
          <Flex align='center' gap='xs'>
            <Label
              name={`${basePath}.cubicFeetDisplay`}
              hideAIHint={true}
              required={false}
              className='text-sm text-neutral-900'
            >
              Cubic Feet
            </Label>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 cursor-help text-neutral-400' />
              </TooltipTrigger>
              <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                <p>
                  Cubic feet for this item (length x width x height x handling
                  units).
                </p>
              </TooltipContent>
            </Tooltip>
          </Flex>
          <Input
            readOnly
            value={
              itemCubicFeet > 0 ? formatUpToFiveDecimals(itemCubicFeet) : '-'
            }
            className='mt-1'
          />
        </div>
        <div>
          <Flex align='center' gap='xs'>
            <Label
              name={`${basePath}.pcfDisplay`}
              hideAIHint={true}
              required={false}
              className='text-sm text-neutral-900'
            >
              Density (PCF)
            </Label>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Info className='h-4 w-4 cursor-help text-neutral-400' />
              </TooltipTrigger>
              <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                <p>
                  Density (PCF) for this item, used to estimate freight class.
                  {densityDerivedFreightClassString
                    ? ` Implied freight class from density: ${densityDerivedFreightClassString}.`
                    : ''}
                </p>
              </TooltipContent>
            </Tooltip>
          </Flex>
          <Input
            readOnly
            value={pcf > 0 ? `${formatUpToFiveDecimals(pcf)} lbs/ft^3` : '-'}
            className='mt-1'
          />
        </div>
      </Grid>

      <Grid cols='2' gap='sm' className='w-full mx-0 mb-3'>
        <div>
          <RHFTextInput
            name={`${basePath}.weightTotal`}
            label='Net Wt'
            inputType='number'
            options={{ valueAsNumber: true, min: 0 }}
            required={false}
          />
        </div>
        <div className='w-full'>
          <Label
            name={`${basePath}.weightUnit`}
            hideAIHint={true}
            className='text-sm text-neutral-900'
          >
            Units
          </Label>
          <Controller
            name={`${basePath}.weightUnit` as FieldPath<NormalizedLoad>}
            control={control}
            render={({ field }) => (
              <div className='mt-1 text-neutral-500'>
                <Select
                  onValueChange={(v) => field.onChange(v ?? undefined)}
                  value={String(field.value ?? 'lbs')}
                >
                  <SelectTrigger className='w-full text-neutral-500'>
                    <SelectValue placeholder='Choose' />
                  </SelectTrigger>
                  <SelectContent>
                    {(weightUnitsList ?? ['lbs', 'kg']).map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          />
        </div>
      </Grid>

      <Grid cols='2' gap='lg' className='w-full mx-0 mb-3'>
        <RHFTextInput
          name={`${basePath}.handlingQuantity`}
          label='HU Count'
          placeholder='1'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
        <div className='w-full'>
          <Label
            name={`${basePath}.packagingType`}
            hideAIHint={true}
            className='text-sm text-neutral-900'
          >
            HU Type
          </Label>
          <Controller
            name={`${basePath}.packagingType` as FieldPath<NormalizedLoad>}
            control={control}
            render={({ field }) => (
              <div className='mt-1 text-neutral-500'>
                <Select
                  onValueChange={field.onChange}
                  value={String(field.value ?? DEFAULT_HU_TYPE_VALUE)}
                >
                  <SelectTrigger className='w-full text-neutral-500'>
                    <SelectValue placeholder='Select type' />
                  </SelectTrigger>
                  <SelectContent>
                    {HANDLING_UNIT_TYPES_THREEG.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          />
        </div>
      </Grid>

      <div className='mb-3'>
        <RHFTextInput
          name={`${basePath}.quantity`}
          label='Piece Count'
          placeholder='1'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
        />
      </div>

      <Grid cols='3' gap='lg' className='w-full mx-0 mb-0'>
        <RHFTextInput
          name={`${basePath}.length`}
          label='Length'
          placeholder='0'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          inlineDimensionUnit='in'
        />
        <RHFTextInput
          name={`${basePath}.width`}
          label='Width'
          placeholder='0'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          inlineDimensionUnit='in'
        />
        <RHFTextInput
          name={`${basePath}.height`}
          label='Height'
          placeholder='0'
          inputType='number'
          options={{ valueAsNumber: true, min: 0 }}
          required={false}
          inlineDimensionUnit='in'
        />
      </Grid>
    </motion.div>
  );

  const renderCollapsedView = () => {
    const netWt = watchedItem?.weightTotal ?? 0;
    const weightUnit = watchedItem?.weightUnit ?? 'lbs';
    const huCount = watchedItem?.handlingQuantity ?? 0;
    const pieceCount = watchedItem?.quantity ?? 0;
    const l = watchedItem?.length ?? 0;
    const w = watchedItem?.width ?? 0;
    const h = watchedItem?.height ?? 0;
    const huTypeValue = watchedItem?.packagingType ?? DEFAULT_HU_TYPE_VALUE;
    const huTypeLabel =
      HANDLING_UNIT_TYPES_THREEG.find((o) => o.value === huTypeValue)?.label ??
      'Pallet';

    const description = watchedItem?.description?.trim();
    const parts: string[] = [];
    if (description) parts.push(description);
    if (Number(netWt) > 0) parts.push(`Net Wt: ${netWt} ${weightUnit}`);
    if (Number(huCount) > 0) parts.push(`${huCount} ${huTypeLabel}`);
    if (Number(pieceCount) > 0) parts.push(`Pieces: ${pieceCount}`);
    if (Number(l) > 0 || Number(w) > 0 || Number(h) > 0) {
      parts.push(`L×W×H: ${l}×${w}×${h} in`);
    }
    const summary = parts.length > 0 ? parts.join(' · ') : 'No data';

    return (
      <motion.div
        key='collapsed-view'
        {...ANIMATIONS.toggle}
        className='overflow-hidden w-full'
      >
        <Flex justify='between' align='center' className='py-2 min-h-[2.5rem]'>
          <Flex align='center' gap='sm' className='flex-1 min-w-0'>
            <Box className='h-6 w-6 flex-shrink-0' />
            <Typography
              variant='caption'
              className='text-neutral-600 font-semibold flex-shrink-0 dark:text-neutral-400'
            >
              {index + 1}
            </Typography>
            <div className='flex-1 min-w-0'>
              <Typography
                variant='body-sm'
                className='text-neutral-900 font-medium truncate dark:text-neutral-100'
              >
                {summary}
              </Typography>
            </div>
          </Flex>
          <div onClick={(e) => e.stopPropagation()}>{renderActions()}</div>
        </Flex>
      </motion.div>
    );
  };

  return (
    <motion.div
      {...ANIMATIONS.card}
      className={cn(
        'w-full rounded-lg border border-neutral-200 bg-neutral-50 p-3 transition-colors dark:border-neutral-700 dark:bg-neutral-800/50',
        isEditing && 'border-brand-main bg-neutral-50 dark:bg-neutral-800/50'
      )}
      onKeyDown={handleKeyDown}
    >
      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderCollapsedView()}
      </AnimatePresence>
    </motion.div>
  );
}
